'use client';

import { useCallback, useMemo, useState } from 'react';

import type { Dispatch, SetStateAction } from 'react';

export interface Cursor {
  hasNext: () => boolean;
  next: () => void;
  hasPrevious: () => boolean;
  previous: () => void;
}

export interface PaginationController {
  page: number | null;
  setPage: Dispatch<SetStateAction<number | null>>;
  setPageCount: Dispatch<SetStateAction<number | null>>;
  cursor: Cursor;
  reset: () => void;
}

export type UsePagination = (
  initialPage?: number,
  initialPageCount?: number
) => PaginationController;

const INITIAL_PAGE = 1;
const INITIAL_PAGE_COUNT = 0;
const ONE_PAGE = 1;

// eslint-disable-next-line max-lines-per-function
export const usePagination: UsePagination = (
  initialPage = INITIAL_PAGE,
  initialPageCount = INITIAL_PAGE_COUNT
) => {
  const [page, setPage] = useState<PaginationController['page']>( initialPage );
  const [pageCount, setPageCount] = useState<number | null>( initialPageCount );

  const hasNext = useCallback( () => {
    if (page === null || pageCount === null) return false;

    return page < pageCount;
  }, [page, pageCount] );

  const hasPrevious = useCallback( () => {
    if (page === null || pageCount === null) return false;

    return page > pageCount;
  }, [page, pageCount] );

  const next = useCallback( () => {
    if (hasNext()) {
      setPage((currPage) => (currPage === null ? currPage : currPage + ONE_PAGE));
    }
  }, [hasNext] );

  const previous = useCallback( () => {
    if (hasPrevious()) {
      setPage((currPage) => (currPage === null ? currPage : currPage - ONE_PAGE));
    }
  }, [hasPrevious] );

  const reset = useCallback( () => setPage( initialPage ), [initialPage] );

  const cursor: Cursor = useMemo(
    () => ({
      hasNext,
      next,
      hasPrevious,
      previous,
    }),
    [hasNext, next, hasPrevious, previous]
  );

  const paginationController: PaginationController = {
    page,
    setPage,
    setPageCount,
    cursor,
    reset,
  };

  return paginationController;
};
